import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isFiniteNumber, readNumber, readString } from '../../shared/checklistUtils';
import type { RecordLike } from '../../shared/checklistUtils';
import { formatMoney } from '../../shared/format';

import { buildCampaignRow } from './campaignRow';
import type { CampaignChecklistRow } from './types';

export function buildSuggestedValueRows(
  packages?: Array<App.Package>,
  opportunityDeals: Array<OpportunityRelatedDeal> = [],
): Array<CampaignChecklistRow> {
  const offerPackages = packages?.length ? packages : [undefined];
  const opportunityDealsById = new Map(opportunityDeals.map((deal) => [String(deal.id), deal]));

  return offerPackages.map((offerPackage, index) => {
    const packageRecord = (offerPackage ?? {}) as unknown as RecordLike;
    const opportunityDeal = offerPackage?.id_salesforce_external
      ? opportunityDealsById.get(String(offerPackage.id_salesforce_external))
      : undefined;
    const dealRecord = (opportunityDeal ?? {}) as unknown as RecordLike;
    const dealCurrencyCode = readString(dealRecord, ['currencyisocode']);
    const priceValue = getPackagePriceValue(packageRecord, dealCurrencyCode);
    const value = readNumber(packageRecord, ['value']) ?? priceValue?.value;
    const suggestedValue =
      readNumber(packageRecord, ['suggested_value__c']) ?? readNumber(dealRecord, ['suggested_value__c']);
    const currencyCode = dealCurrencyCode ?? priceValue?.currencyCode;
    const approved = isFiniteNumber(suggestedValue) && isFiniteNumber(value) && suggestedValue === value;
    const packageName =
      offerPackage?.deal_option_name ||
      offerPackage?.name ||
      opportunityDeal?.name ||
      (offerPackage ? `Deal option ${index + 1}` : undefined);

    return buildCampaignRow({
      approved,
      dataPoints: [
        { label: 'Suggested value', value: formatOptionalCurrency(suggestedValue, currencyCode) },
        { label: 'Value', value: formatOptionalCurrency(value, currencyCode) },
      ],
      evidence: getSuggestedValueEvidence({ suggestedValue, value }),
      field: 'Suggested value',
      id: `suggested_value:deal_offer__c:${String(offerPackage?.id_salesforce_external ?? index)}`,
      packageName,
      rule: 'Compare Suggested value with Value in cost currency.',
      source: 'Opportunity deal suggested_value__c / offer package price value',
      value: `Suggested value: ${formatOptionalCurrency(suggestedValue, currencyCode)} / Value: ${formatOptionalCurrency(
        value,
        currencyCode,
      )}`,
    });
  });
}

function getSuggestedValueEvidence({ suggestedValue, value }: { suggestedValue?: number; value?: number }) {
  if (!isFiniteNumber(suggestedValue)) {
    return 'Suggested value is missing from opportunity deal data.';
  }

  if (!isFiniteNumber(value)) {
    return 'Value is missing from package price data.';
  }

  return suggestedValue === value ? 'Suggested value matches Value.' : 'Suggested value does not match Value.';
}

function formatOptionalCurrency(value?: number, currencyCode?: string) {
  if (!isFiniteNumber(value)) {
    return 'N/A';
  }

  return currencyCode ? formatMoney(value, currencyCode) : String(value);
}

function getPackagePriceValue(packageRecord: RecordLike, currencyCode?: string) {
  const prices = packageRecord.prices;

  if (!Array.isArray(prices)) {
    return undefined;
  }

  const preferredPrice = currencyCode
    ? prices.find((price) => readString(price as RecordLike, ['currency_code']) === currencyCode)
    : undefined;
  const sortedPrices = preferredPrice
    ? [preferredPrice, ...prices.filter((price) => price !== preferredPrice)]
    : prices;

  for (const price of sortedPrices) {
    const priceRecord = price as RecordLike;
    const value = readNumber(priceRecord, ['value']);

    if (isFiniteNumber(value)) {
      return {
        currencyCode: readString(priceRecord, ['currency_code']),
        value,
      };
    }
  }

  return undefined;
}
